import { get } from 'lodash';
import React, { Component } from 'react';
import {
    CARRIER,
    HAS_SET_SELF_PAY,
    IM_PAYING_MYSELF,
} from '@zocdoc-frontend-common/components-insuranceform';
import { PatientRadioValues } from '../../components/NewOrExistingPatient';
import { BookingStateProperties, BOOKING_STATUS } from '../../constants';
import { insuranceValidForCheckingAcceptance } from '../../utils';

/**
 * @param {Object} bookingState
 * @param {function(): void} clearTimeslotConstraint
 * @param {function(bookingId: string): void} getBookingState
 * @param {boolean} hasAppointmentTimeError
 * @param {string} locationTimeZoneId
 * @param {boolean} procedureMayNotBeCovered
 * @param {Object[]} procedures
 * @param {Object} professional
 * @param {function(carrierId: string): void} setCarrierState
 * @param {function(planId: string): void} setPlanState
 * @param {function(): void} setTimeslotConstraint
 * @param {boolean} showTimesGrid
 * @param {function(): void} trackCardCaptureEligibleAtBooking
 * @param {function(): void} trackChangeAppointmentTimeClick
 * @param {function(): void} trackChangeToExistingPatient
 * @param {function(): void} trackChangeToNewPatient
 * @param {function(): void} trackClickContinue
 * @param {function(): void} trackDisplaySelectNewTimePointer
 * @param {function(): void} trackHasSeenThisDoctorBefore
 * @param {function(): void} trackInsuranceCardAttachedWithBooking
 * @param {function(): void} trackIsNewPatient
 * @param {function(): void} trackProcedureMayNotBeCoveredTooltipDisplayed
 * @param {function(): void} trackSelectAppointmentTime
 * @param {function(bookingState: string): void} updateBookingState
 * @param {function(isExistingPatient: boolean): void} updateExistingPatientState
 * @returns {JSX.Element}
 * @constructor
 */
export class AppointmentInfoStateManager extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showTimesGrid: Boolean(props.showTimesGrid),
            hasAppointmentTimeError: Boolean(props.hasAppointmentTimeError),
            continueClicked: false,
        };

        if (props.procedureMayNotBeCovered) {
            props.trackProcedureMayNotBeCoveredTooltipDisplayed();
        }
    }

    validateState = () => {
        const { existingPatient, procedureId } = this.destructureBookingState(
            this.props.bookingState
        );

        const validProcedureConfigured = procedureId && procedureId !== '-1';
        if (validProcedureConfigured && existingPatient !== undefined) {
            return true;
        }

        return false;
    };

    callUpdateBookingState = (updatedProperties) => {
        const { bookingState, updateBookingState } = this.props;
        if (bookingState) {
            updateBookingState({
                ...bookingState,
                ...updatedProperties,
            });
        }
    };

    handleRadioChange = (patientRadioValue) => {
        const {
            bookingState,
            updateExistingPatientState,
            trackChangeToNewPatient,
            trackChangeToExistingPatient,
        } = this.props;

        const isExistingPatient =
            patientRadioValue === PatientRadioValues.EXISTING_PATIENT;

        if (bookingState) {
            updateExistingPatientState(isExistingPatient);
        }

        if (isExistingPatient) {
            trackChangeToExistingPatient();
        } else {
            trackChangeToNewPatient();
        }
    };

    handleEditClick = () => {
        const { trackChangeAppointmentTimeClick } = this.props;
        this.setState({
            showTimesGrid: true,
            hasAppointmentTimeError: false,
        });

        trackChangeAppointmentTimeClick();
    };

    handleContinueClick = () => {
        const {
            bookingState,
            showInsurancePickerError,
            hasAppointmentTimeError,
            trackCardCaptureEligibleAtBooking,
            trackClickContinue,
            trackInsuranceCardAttachedWithBooking,
        } = this.props;

        const insuranceInfoIsValid =
            insuranceValidForCheckingAcceptance(bookingState) ||
            bookingState[BookingStateProperties.insuranceCarrierId] ===
                IM_PAYING_MYSELF;

        if (insuranceInfoIsValid && !hasAppointmentTimeError) {
            this.setState({ continueClicked: true });
            trackClickContinue();

            trackCardCaptureEligibleAtBooking();
            if (!!bookingState[BookingStateProperties.insuranceCardDataId]) {
                trackInsuranceCardAttachedWithBooking();
            }

            this.callUpdateBookingState();
        } else if (!insuranceInfoIsValid) {
            showInsurancePickerError();
        }
    };

    checkResourceConstraint = (timeslot, event) => {
        event.preventDefault();
        const { clearTimeslotConstraint, setTimeslotConstraint } = this.props;

        if (timeslot.isResource) {
            setTimeslotConstraint();
        } else {
            clearTimeslotConstraint();
        }
        this.setState({ showTimesGrid: false });
        this.handleTimeSlotClick(timeslot);
    };

    handleTimeSlotClick = (timeslot) => {
        const { trackSelectAppointmentTime } = this.props;
        this.setState({
            showTimesGrid: false,
        });
        trackSelectAppointmentTime();

        let newAppointmentTime;

        newAppointmentTime = timeslot.startTime;

        this.callUpdateBookingState({
            [BookingStateProperties.appointmentTime]: newAppointmentTime,
        });
    };

    handleChangeCarrier = (carrierId) => {
        const { setCarrierState } = this.props;
        window.sessionStorage.setItem(
            HAS_SET_SELF_PAY,
            carrierId === CARRIER.IM_PAYING_MYSELF
        );
        setCarrierState(carrierId);
    };

    handleChangePlan = (planId) => {
        const { setPlanState } = this.props;

        setPlanState(planId);
    };

    handleClickCarrier = () => {
        const { trackClickCarrierDropdown } = this.props;
        trackClickCarrierDropdown();
    };

    handleClickPlan = () => {
        const { trackClickPlanDropdown } = this.props;
        trackClickPlanDropdown();
    };

    componentDidMount() {
        this.props.trackMadeItToBookingFlow();
    }

    componentWillReceiveProps(nextProps) {
        const {
            trackDisplaySelectNewTimePointer,
            trackIsNewPatient,
            trackHasSeenThisDoctorBefore,
            trackProcedureMayNotBeCoveredTooltipDisplayed,
            procedureMayNotBeCovered: oldProcedureMayNotBeCovered,
            bookingState: oldBookingState,
        } = this.props;

        const {
            showTimesGrid,
            hasAppointmentTimeError,
            procedureMayNotBeCovered: newProcedureMayNotBeCovered,
            bookingState: newBookingState,
        } = nextProps;

        const { continueClicked } = this.state;
        const updatedBookingStateProperties = {};

        if (continueClicked && !showTimesGrid && oldBookingState) {
            if (!newBookingState[BookingStateProperties.existingPatient]) {
                trackIsNewPatient();
            } else {
                trackHasSeenThisDoctorBefore();
            }

            this.props.onValidContinue();
        } else if (oldBookingState || newBookingState) {
            const { procedureId: oldProcedureId } =
                this.destructureBookingState(oldBookingState);

            const { procedureId: newProcedureId } =
                this.destructureBookingState(newBookingState);

            // the view never updates the remote booking state with these values, do it here if they've changed
            if (oldProcedureId !== newProcedureId) {
                updatedBookingStateProperties[
                    BookingStateProperties.procedureId
                ] = newProcedureId;
            }
        }

        if (showTimesGrid) {
            this.setState({ continueClicked: false });
        }

        if (hasAppointmentTimeError && !this.props.hasAppointmentTimeError) {
            trackDisplaySelectNewTimePointer();
        }

        if (
            newProcedureMayNotBeCovered &&
            oldProcedureMayNotBeCovered !== newProcedureMayNotBeCovered
        ) {
            trackProcedureMayNotBeCoveredTooltipDisplayed();
        }

        const newProcedureId = get(
            newBookingState,
            BookingStateProperties.procedureId
        );
        if (
            !(parseInt(newProcedureId, 10) > 0) &&
            nextProps.defaultProcedureId
        ) {
            updatedBookingStateProperties[BookingStateProperties.procedureId] =
                nextProps.defaultProcedureId;
        }

        const newBookingStatus = get(
            newBookingState,
            BookingStateProperties.status
        );

        if (
            Object.keys(updatedBookingStateProperties).length > 0 &&
            newBookingStatus !== BOOKING_STATUS.COMPLETE
        ) {
            this.callUpdateBookingState(updatedBookingStateProperties);
        }
    }

    destructureBookingState = (bookingState) => {
        if (bookingState) {
            let planId = bookingState[BookingStateProperties.insurancePlanId];
            planId = planId === '-1' ? undefined : planId;

            return {
                appointmentTime:
                    bookingState[BookingStateProperties.appointmentTime],
                existingPatient:
                    bookingState[BookingStateProperties.existingPatient],
                iceRequestToken:
                    bookingState[BookingStateProperties.iceRequestToken],
                insuranceCarrierId:
                    bookingState[BookingStateProperties.insuranceCarrierId],
                insurancePlanId: planId,
                locationId: bookingState[BookingStateProperties.locationId],
                procedureId: bookingState[BookingStateProperties.procedureId],
                providerId: bookingState[BookingStateProperties.providerId],
            };
        } else return {};
    };

    render() {
        const {
            insuranceIdRequired,
            locationTimeZoneId,
            patientDateOfBirth,
            patientFirstName,
            patientLastName,
            procedureMayNotBeCovered,
            procedures,
            professionalName,
            showTimesGrid: showTimesGridFromProps,
            hasAppointmentTimeError: hasAppointmentTimeErrorFromProps,
            specialtyId,
            trackPreloadInsuranceCarrier,
            trackPreloadInsurancePlan,
            ...otherProps
        } = this.props;

        const {
            showTimesGrid: showTimesGridFromState,
            hasAppointmentTimeError: hasAppointmentTimeErrorFromState,
        } = this.state;

        const {
            appointmentTime,
            existingPatient,
            iceRequestToken,
            insuranceCarrierId,
            insurancePlanId,
            locationId,
            procedureId,
            providerId,
        } = this.destructureBookingState(this.props.bookingState);

        const disableContinue = !this.validateState();
        const procedure = get(procedures, [procedureId]);
        const procedureName = get(procedure, 'name');
        const showEmergencyNotice = get(procedure, 'show911Notice');

        const childProps = {
            appointmentTime,
            disableContinue,
            iceRequestToken,
            insuranceCarrierId,
            // While the below two are duplicates of `insuranceCarrierId` and `insurancePlanId`,
            // we need these props with keys `carrierId` and `planId`, so
            // `withInsuranceValidation` will work. Changing the `insuranceCarrierId`
            // and `insurancePlanId` prop names will mess with the booking state selectors,
            // which I don't want to break/refactor at this point.
            carrierId: insuranceCarrierId,
            planId: insurancePlanId,
            insuranceIdRequired,
            insurancePlanId,
            isNewPatient: !existingPatient,
            locationId,
            locationTimeZoneId,
            onChangeCarrier: this.handleChangeCarrier,
            onChangePlan: this.handleChangePlan,
            onClickCarrier: this.handleClickCarrier,
            onClickPlan: this.handleClickPlan,
            onContinueClick: this.handleContinueClick,
            onEditClick: this.handleEditClick,
            onRadioChange: this.handleRadioChange,
            onTimeSlotClicked: this.checkResourceConstraint,
            patientDateOfBirth,
            patientFirstName,
            patientLastName,
            procedureId,
            procedureMayNotBeCovered,
            procedureName,
            professionalName,
            providerId,
            showEmergencyNotice,
            showTimesGrid: showTimesGridFromProps || showTimesGridFromState,
            hasAppointmentTimeError:
                hasAppointmentTimeErrorFromProps ||
                hasAppointmentTimeErrorFromState,
            specialtyId,
            trackPreloadInsuranceCarrier,
            trackPreloadInsurancePlan,
        };

        return React.Children.map(this.props.children, (child) =>
            React.cloneElement(child, {
                ...otherProps,
                appointmentInfo: { ...childProps },
                children: child.props.children,
                specialtyId,
            })
        );
    }
}

export default AppointmentInfoStateManager;
